import { useState } from "react";
import type { WizardController } from "../../../hooks/use-wrapped-api-wizard";
import { WizardField, wizardStyles } from "./wizard-ui";

/**
 * Step 1 — Spec. Paste an OpenAPI 3.0/3.1 URL (or the raw JSON/YAML for analysis)
 * and the shell's "Analyse spec" button runs `wrap_api_from_openapi`. The
 * analysis pre-fills every later step.
 */
export function Step1Spec({ wizard }: { wizard: WizardController }) {
  const [showPaste, setShowPaste] = useState(false);

  return (
    <div>
      <p style={wizardStyles.intro}>
        Point at an OpenAPI spec and Kuvio's AI proposes a complete setup — slug, auth, which operations to enable,
        and how responses map into your entity types. You accept or edit each proposal; nothing is created until you say so.
      </p>

      <WizardField label="OpenAPI spec URL" hint="The spec is fetched server-side — large specs never touch your browser.">
        <input
          value={wizard.specUrl}
          onChange={(e) => wizard.setSpecUrl(e.target.value)}
          placeholder="https://developer.zendesk.com/zendesk/oas.yaml"
          style={wizardStyles.input}
          autoFocus
        />
      </WizardField>

      <details open={showPaste} onToggle={(e) => setShowPaste((e.target as HTMLDetailsElement).open)}>
        <summary style={{ fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer", marginBottom: 8 }}>
          Or paste the spec JSON / YAML
        </summary>
        <textarea
          value={wizard.specJson}
          onChange={(e) => wizard.setSpecJson(e.target.value)}
          placeholder={'{ "openapi": "3.0.0", "info": { ... }, "paths": { ... } }'}
          rows={8}
          style={{ ...wizardStyles.input, fontFamily: "var(--font-mono)", fontSize: 11, resize: "vertical" }}
        />
        <div style={{ ...wizardStyles.hint, marginTop: 4 }}>
          Works for private or internal specs with no public URL. Note: a pasted spec can't auto-refresh — use a URL if you want Kuvio to re-fetch it as the API evolves.
        </div>
      </details>

      {wizard.analyzing && (
        <div style={loadingBox}>
          <i className="ti ti-loader-2 ti-spin" style={{ fontSize: 14 }} aria-hidden="true" />
          Analysing the spec with AI — detecting auth, classifying operations, and proposing mappings…
        </div>
      )}
    </div>
  );
}

const loadingBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  color: "var(--color-text-secondary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "10px 12px",
  marginTop: 12,
} as const;
